import Autocomplete from "@mui/material/Autocomplete";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import { createFilterOptions } from "@mui/material";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import IndividualSummary from "marketplace/individuals/profiles/summaries/individual-summary";
import { useEffect, useMemo } from "react";
import { useSession } from "users/__session/session-context";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { useSelector } from "react-redux";
import {
  getErrorLoadingIndividualsByEntity,
  getIndividualsByEntity,
  getIndividualsIsLoadingByEntity,
  populateIndividualsByEntity
} from "marketplace/individuals/_stores/individuals-redux-slice";

const Container = styled(FormControl)(() => ({
  maxWidth: '100%',
  minWidth: '33%'
}));

type CompanyProfileContactPersonInputProps = {
  className?: string;
  required?: boolean;
  contactUserId?: Guid;
  onChange: (value: IndividualSummary | null) => void;
};

export default function CompanyProfileContactPersonInput(
  props: Readonly<CompanyProfileContactPersonInputProps>) {
  const {
    className,
    required,
    contactUserId,
    onChange
  } = props;

  // #region Hooks
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  const legalEntityId = session.user!.legalEntityId;

  // #region Selectors
  const coworkers = useSelector(getIndividualsByEntity(legalEntityId));
  const isLoadingCoworkers = useSelector(getIndividualsIsLoadingByEntity(legalEntityId));
  const errorLoadingCoworkers = useSelector(getErrorLoadingIndividualsByEntity(legalEntityId));

  // #region Lifecycle
  useEffect(() => {
    if (coworkers?.length || isLoadingCoworkers || errorLoadingCoworkers) return;
    dispatch(populateIndividualsByEntity({ session, legalEntityId }));
  }, [
    coworkers?.length,
    isLoadingCoworkers,
    errorLoadingCoworkers,
    dispatch,
    session,
    legalEntityId
  ]);

  // Derive contactPerson directly from coworkers and contactUserId
  const contactPerson = useMemo(() => {
    if (!contactUserId) return null;
    return coworkers?.find((c) => c.userId.isEqualTo(contactUserId)) ?? null;
  }, [coworkers, contactUserId]);
  // #endregion

  function getOptionLabel(option: unknown) {
    if (!option) return 'None';
    const coworker = option as IndividualSummary;
    return `${coworker.firstName} ${coworker.lastName}`;
  }

  const filterOptions = useMemo(
    () =>
      createFilterOptions<IndividualSummary>({
        stringify: (c) => `${c.firstName} ${c.lastName} ${c.emailAddress ?? ""}`
      }),
    []
  );

  // #region Render
  return (
    <Container className={className}>
      <Autocomplete
        getOptionLabel={getOptionLabel}
        onChange={(_event, value) => {
          onChange(value);
        }}
        value={contactPerson}
        options={coworkers ?? []}
        isOptionEqualToValue={(option: unknown, value: unknown) => {
          if (!option || !value) return false;
          const coworker = option as IndividualSummary;
          const selectedCoworker = value as IndividualSummary;
          return coworker.id.isEqualTo(selectedCoworker.id);
        }}
        loading={isLoadingCoworkers}
        filterOptions={filterOptions}
        noOptionsText="No matching coworkers found"
        renderOption={(liProps, coworker) => (
          <ListItem {...liProps} key={coworker.id.value}>
            <ListItemAvatar>
              <Avatar>
                {`${coworker.firstName.slice(0, 1)}${coworker.lastName.slice(0, 1)}`}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={`${coworker.firstName} ${coworker.lastName}`}
              secondary={coworker.emailAddress ? coworker.emailAddress.toString() : ""}
            />
          </ListItem>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            margin="none"
            variant="outlined"
            required={required}
            label="Contact Person"
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoadingCoworkers
                      ? <CircularProgress color="inherit" size={20} disableShrink />
                      : null
                    }
                    {params.InputProps.endAdornment}
                  </>
                )
              }
            }}
          />
        )}
      />
    </Container>
  );
}
